import { createTheme } from '@mui/material/styles';

/**
 * Amabec B2B Portal — Industrial Pro theme.
 *
 * Direction (design doc §plan-design-review): Home Depot Pro / Grainger /
 * McMaster-Carr. Sobre, dense, fonctionnel. PAS de gradients, PAS de
 * shadows décoratifs, PAS de violet SaaS. Inter pour tout le texte,
 * tabular-nums pour prix + stock pour l'alignement des colonnes.
 * Border radius 3-4px. Focus visible 2px outline pour a11y.
 */

export const b2bColors = {
    primary: '#1e40af',       // bleu industriel (AAA sur blanc)
    primaryHover: '#1d3a96',
    primaryLight: '#dbeafe',
    accent: '#ca8a04',        // jaune "promo" sobre, pour tags "Nouveau"
    ink: '#0f172a',           // texte principal
    textMuted: '#475569',     // texte secondaire (AA sur blanc: 4.8:1)
    textDisabled: '#94a3b8',  // décoratif uniquement (2.7:1 — pas informatif)
    border: '#e2e8f0',
    borderStrong: '#cbd5e1',
    surface: '#ffffff',
    surfaceAlt: '#f6f7f9',
    surfaceHover: '#f1f5f9',
    danger: '#b91c1c',
    success: '#15803d',
    warning: '#c2410c',
};

export const b2bTheme = createTheme({
    palette: {
        mode: 'light',
        primary: { main: b2bColors.primary, dark: b2bColors.primaryHover, light: b2bColors.primaryLight, contrastText: '#fff' },
        secondary: { main: b2bColors.accent, contrastText: '#fff' },
        error: { main: b2bColors.danger },
        success: { main: b2bColors.success },
        warning: { main: b2bColors.warning },
        background: { default: b2bColors.surfaceAlt, paper: b2bColors.surface },
        text: { primary: b2bColors.ink, secondary: b2bColors.textMuted, disabled: b2bColors.textDisabled },
        divider: b2bColors.border,
    },
    shape: { borderRadius: 3 },
    typography: {
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        fontSize: 14,
        h1: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.01em' },
        h2: { fontSize: '1.4rem', fontWeight: 700, letterSpacing: '-0.01em' },
        h3: { fontSize: '1.2rem', fontWeight: 600 },
        h4: { fontSize: '1.05rem', fontWeight: 600 },
        h5: { fontSize: '0.95rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: b2bColors.textMuted },
        body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
        body2: { fontSize: '0.875rem', lineHeight: 1.5, color: b2bColors.textMuted },
        button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                '*:focus-visible': {
                    outline: `2px solid ${b2bColors.primary}`,
                    outlineOffset: '2px',
                },
                // Tabular numerals for prices, stock, SKU alignment
                '.b2b-num, td.b2b-num, th.b2b-num': { fontVariantNumeric: 'tabular-nums' },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: 3,
                    minHeight: 40,
                    paddingLeft: 16,
                    paddingRight: 16,
                },
                containedPrimary: {
                    boxShadow: 'none',
                    '&:hover': { boxShadow: 'none', backgroundColor: b2bColors.primaryHover },
                },
                outlined: {
                    borderColor: b2bColors.borderStrong,
                    color: b2bColors.ink,
                    '&:hover': { borderColor: b2bColors.primary, backgroundColor: b2bColors.surfaceHover },
                },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: { border: `1px solid ${b2bColors.border}`, backgroundImage: 'none' },
            },
        },
        MuiAppBar: {
            defaultProps: { elevation: 0, color: 'default' },
            styleOverrides: {
                root: { backgroundColor: b2bColors.surface, borderBottom: `1px solid ${b2bColors.border}` },
            },
        },
        MuiTextField: {
            defaultProps: { variant: 'outlined', size: 'small', fullWidth: true },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 3,
                    backgroundColor: b2bColors.surface,
                    '& fieldset': { borderColor: b2bColors.borderStrong },
                    '&:hover fieldset': { borderColor: b2bColors.primary },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottomColor: b2bColors.border,
                    padding: '10px 12px',
                },
                head: {
                    fontWeight: 600,
                    color: b2bColors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    fontSize: '0.75rem',
                    backgroundColor: b2bColors.surfaceAlt,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: 3, fontWeight: 500 },
            },
        },
        MuiLink: {
            defaultProps: { underline: 'hover' },
            styleOverrides: {
                root: { color: b2bColors.primary, fontWeight: 500 },
            },
        },
    },
});
